import { useState } from 'react';
import { add, MetadataType, MetadataTypeSchema } from '@/api/doc/metadata-type';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '@/components/ui/sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CirclePlus } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function AddMetadataType() {
	const [isSheetOpen, setIsSheetOpen] = useState(false);
	const queryClient = useQueryClient();

	const form = useForm<MetadataType>({
		resolver: zodResolver(MetadataTypeSchema),
		defaultValues: {
			id: null,
			name: '',
			slug: '',
			deletedOn: null,
		},
		mode: 'all',
	});

	const { mutate } = useMutation({
		mutationFn: (newMetadataType: MetadataType) => add(newMetadataType),
		onSuccess: () => {
			toast.success(`Success! The metadata type has been successfully added`, {
				dismissible: true,
			});
			setIsSheetOpen(false);
			queryClient.invalidateQueries({ queryKey: ['metadata-types'] });
		},
		onError: () => {
			toast.error(`Error! The metadata type could not be added`, {
				dismissible: true,
			});
		},
	});
	const onSubmit: SubmitHandler<MetadataType> = (values: MetadataType) =>
		mutate(values);

	form.watch();

	return (
		<Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
			<SheetTrigger asChild>
				<Button>
					<CirclePlus />
					Add Metadata Type
				</Button>
			</SheetTrigger>
			<SheetContent className="overflow-scroll">
				<SheetHeader>
					<SheetTitle>Add Metadata Type</SheetTitle>
				</SheetHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Metadata Type Name</FormLabel>
									<FormControl>
										<Input {...field} placeholder="Metadata type name" />
									</FormControl>
									<FormDescription>Name the metadata type</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Button
							type="submit"
							disabled={!form.formState.isValid || form.formState.isSubmitting}
						>
							{form.formState.isSubmitting ? 'Adding...' : 'Add'}
						</Button>
					</form>
				</Form>
			</SheetContent>
		</Sheet>
	);
}
